import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { QueryClient, QueryClientProvider } from 'react-query';
import { SupabaseProvider } from './contexts/SupabaseContext';
import { AuthProvider } from './contexts/AuthContext';
import Layout from './components/Layout/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import FormBuilder from './pages/FormBuilder';
import FormResponses from './pages/FormResponses';
import Devices from './pages/Devices';
import StorageConfig from './pages/StorageConfig';
import SystemStatus from './pages/SystemStatus';

// Create a medical spa theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#2E7D8A', // Soft teal blue
      light: '#5BA3B3',
      dark: '#1A5A6B',
    },
    secondary: {
      main: '#7FB069', // Soft sage green
      light: '#A3C893',
      dark: '#5A8A4A',
    },
    background: {
      default: '#F8FAFC', // Very light blue-gray
      paper: '#FFFFFF',
    },
    text: {
      primary: '#2C3E50', // Dark blue-gray
      secondary: '#5A6C7D', // Medium blue-gray
    },
    success: {
      main: '#27AE60', // Soft green
    },
    warning: {
      main: '#F39C12', // Soft orange
    },
    error: {
      main: '#E74C3C', // Soft red
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
      color: '#2C3E50',
    },
    h6: {
      fontWeight: 500,
      color: '#2C3E50',
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#2E7D8A',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#FFFFFF',
          borderRight: '1px solid #E8EDF2',
        },
      },
    },
  },
});

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <SupabaseProvider>
        <AuthProvider>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <Router>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/" element={
                  <ProtectedRoute>
                    <Layout>
                      <Dashboard />
                    </Layout>
                  </ProtectedRoute>
                } />
                <Route path="/forms" element={
                  <ProtectedRoute>
                    <Layout>
                      <FormBuilder />
                    </Layout>
                  </ProtectedRoute>
                } />
                <Route path="/responses" element={
                  <ProtectedRoute>
                    <Layout>
                      <FormResponses />
                    </Layout>
                  </ProtectedRoute>
                } />
                <Route path="/devices" element={
                  <ProtectedRoute>
                    <Layout>
                      <Devices />
                    </Layout>
                  </ProtectedRoute>
                } />
                <Route path="/storage" element={
                  <ProtectedRoute>
                    <Layout>
                      <StorageConfig />
                    </Layout>
                  </ProtectedRoute>
                } />
                <Route path="/system" element={
                  <ProtectedRoute>
                    <Layout>
                      <SystemStatus />
                    </Layout>
                  </ProtectedRoute>
                } />
              </Routes>
            </Router>
          </ThemeProvider>
        </AuthProvider>
      </SupabaseProvider>
    </QueryClientProvider>
  );
}

export default App;
